export const dropdownStyles = (isProfile = false) => {
    return {
        dropdownIndicator: (base) => ({
            ...base,
            display: 'none'
        }),
        input: (base) => ({
            ...base,
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '100%',
            color: '#ffffff',
            outline: 'none',
            background: 'transparent',
            padding: 0,
            margin: 0
        }),
        control: (base) => ({
            ...base,
            height: '48px',
            width: '100%',
            maxWidth: '375px',
            background: isProfile ? 'transparent' : '#4743A2',
            border: '1px solid #7D61A7',
            borderRadius: '65px',
            padding: '5px 15px'
        }),
        indicatorSeparator: () => null,
        clearIndicator: (base) => ({
            ...base,
            paddingRight: 0
        }),
        placeholder: (base) => ({
            ...base,
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '100%',
            opacity: 1,
            color: '#AEAEC1'
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '5px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            marginTop: '5px',
            background: '#4743A2',
            border: '1px solid #7D61A7',
            padding: 0,
            zIndex: 20
        }),
        option: (base, { isFocused }) => ({
            ...base,
            margin: 0,
            color: isFocused ? '#4743A2' : '#ffffff',
            backgroundColor: isFocused ? '#ffffff' : 'transparent',
            ':hover': {
                backgroundColor: '#ffffff',
                color: '#4743A2'
            }
        }),
        singleValue: (base) => ({
            ...base,
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '100%',
            color: '#ffffff',
            gridArea: '1/1/1/1'
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '2px 0'
        })
    };
};

/**
 * Profile v2 field styling (HORO-10533).
 *
 * The birth-place field has to look identical to the plain email input beside
 * it — Figma 5211:21878 and 5475:33498 are the same field with a different
 * label. `dropdownStyles` above is the legacy onboarding look (a 65px pill,
 * fixed 375px cap, purple fill) and is left untouched for its existing
 * callers; this is a separate style set rather than another boolean flag on
 * that one, which already branches on a pathname check.
 */
export const profileFieldStyles = () => ({
    dropdownIndicator: () => ({ display: 'none' }),
    indicatorSeparator: () => null,
    clearIndicator: (base) => ({ ...base, paddingRight: 0 }),
    control: (base) => ({
        ...base,
        minHeight: '56px',
        width: '100%',
        background: 'transparent',
        border: '1px solid var(--c-profile-field-border)',
        borderRadius: '8px',
        // Right padding leaves room for the pencil the field wrapper overlays.
        padding: '4px 48px 4px 14px',
        boxShadow: 'none',
        ':hover': { border: '1px solid var(--c-profile-field-border)' }
    }),
    input: (base) => ({
        ...base,
        fontFamily: 'Roboto',
        fontWeight: 400,
        fontSize: '16px',
        color: '#ffffff',
        margin: 0,
        padding: 0
    }),
    singleValue: (base) => ({
        ...base,
        fontFamily: 'Roboto',
        fontWeight: 400,
        fontSize: '16px',
        color: '#ffffff',
        gridArea: '1/1/1/1'
    }),
    placeholder: (base) => ({
        ...base,
        fontFamily: 'Roboto',
        fontSize: '16px',
        color: 'rgba(255, 255, 255, 0.5)'
    }),
    valueContainer: (base) => ({ ...base, padding: 0 }),
    menu: (base) => ({
        ...base,
        marginTop: '6px',
        background: '#171A2B',
        border: '1px solid var(--c-profile-field-border)',
        borderRadius: '8px',
        overflow: 'hidden',
        padding: 0,
        zIndex: 20,
        // Mobile: in the flow rather than overlaid on it.
        //
        // The editor is a bottom sheet sized to its own content. An absolutely
        // positioned menu contributes no height, so the sheet stays short and
        // the suggestions open straight off the bottom of the screen. In flow,
        // opening the list grows the sheet by exactly the list's height and
        // closing it shrinks back — no dead space at rest, which a taller
        // fixed sheet would have left.
        //
        // `top` has to be reset with it: react-select's `top: 100%` means
        // "below the control" only while the menu is out of flow. Left as-is it
        // would push the menu down by its own height once relative.
        //
        // Desktop keeps the overlay — that pane is a full column and has the
        // room, and reflowing it would shove the Update button around for no
        // reason. 769px matches the `responsive-below` breakpoint the profile
        // stylesheets use.
        '@media (max-width: 769px)': {
            position: 'relative',
            top: 'auto'
        }
    }),
    // react-select's 300px default is more than the sheet should grow by in one
    // go on a short phone. Google returns at most five predictions, which fit
    // inside this; anything longer scrolls within the menu.
    menuList: (base) => ({ ...base, maxHeight: '240px', padding: 0 }),
    option: (base, { isFocused }) => ({
        ...base,
        color: '#ffffff',
        backgroundColor: isFocused ? 'rgba(165, 162, 231, 0.16)' : 'transparent',
        cursor: 'pointer'
    })
});
